import type {TypographyWeights} from '@gwc/core'
import type {SlotSize} from '@yandex-delivery/blocks'

import type {ActionConfig, CounterActionConfig} from '../actions/types'
import type {EventGoal} from '../lib/metrika'

export enum WidgetType {
  ACTION_BUTTON = 'action_button',
  ADDRESS = 'address',
  DIVIDER = 'divider',
  FORM = 'form',
  HEADER = 'header',
  ICON_BUTTON = 'icon_button',
  ICON_SPOT = 'icon_spot',
  IMAGE = 'image',
  NAV = 'nav',
  PAYMENT_METHOD = 'payment_method',
  RADIO = 'radio',
  SEGMENT = 'segment',
  SLOT = 'slot',
  SWITCH = 'switch',
  TAG_LINE = 'tag_line',
  TEXT = 'text',
  DROPDOWN_LIST = 'dropdown_list',
  ICON = 'icon',
  TEXT_INPUT = 'text_input',
  PHONE_INPUT = 'phone_input',
  COST_DETAIL = 'cost_detail',
  COUNTER = 'counter',
  VERTICALS = 'verticals',
}

export type BaseWidgetConfig = {
  type: WidgetType
  widget_id?: string
  metrica_label?: string
}

export type Image = {image_tag: string}

export type ActionButtonStyle = {
  slot: {
    align: string
    title: {
      items: Array<WidgetConfig>
    }
    subtitle?: {
      items: Array<WidgetConfig>
    }
  }
  style: 'main'
  enabled: boolean
}

export type ActionButtonWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.ACTION_BUTTON
  button_size: SlotSize
  normal_style: ActionButtonStyle
  loading_style: ActionButtonStyle
  action?: ActionConfig
  metrica_label?: string
}

export type AddressWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.ADDRESS
  route_point_index: number
  address_position: 'title' | 'subtitle'
  selected_description: {
    items: Array<TextWidgetConfig>
  }
  unselected_description: {
    items: Array<TextWidgetConfig>
  }
  address_style?: {
    size: number
    color: 'textMain'
  } // TextParameters
  // flow?: AddressFlow
  selected_lead?: IconSpotWidgetConfig
  unselected_lead?: IconSpotWidgetConfig
  trail?: WidgetConfig
  trail2?: NavWidgetConfig
  metrica_label?: EventGoal | string
}

export type Vertical = {
  icon: Image
  title?: {
    items: Array<TextWidgetConfig>
  }
  subtitle?: {
    items: Array<TextWidgetConfig>
  }
  value: string
  active?: boolean
  familiar_vertical_id?: string
  default_values: {
    [x: string]: unknown
  }
  action_instead_of_default?: ActionConfig
}

export type VerticalsWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.VERTICALS
  form_state_key: string
  options: Vertical[]
}

type DividerBaseWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.DIVIDER
}

type DividerSpacerWidgetConfig = DividerBaseWidgetConfig & {
  style: 'spacer'
  spacer_height: number
}

type DividerLineMarginWidgetConfig = BaseWidgetConfig & {
  style: 'line_margin'
}

type DividerLineSlotMarginWidgetConfig = BaseWidgetConfig & {
  style: 'line_slot_margin'
}

export type DividerWidgetConfig =
  | DividerSpacerWidgetConfig
  | DividerLineMarginWidgetConfig
  | DividerLineSlotMarginWidgetConfig

export type Section = {
  widgets: Array<WidgetConfig>
}

export type SectionProps = {
  section: Section
}

export type FormWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.FORM
  header_section?: Section
  sections: Array<Section>
  bottom_section?: Section
  metrica_label?: EventGoal | string
}

export type HeaderWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.HEADER
  image: Image
  title: {
    items: Array<TextWidgetConfig>
  }
  subtitle_style: {
    size: number
    color: string
  }
  trail?: IconButtonWidgetConfig
}

export type IconWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.ICON
  icon: Image
}

export type IconButtonWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.ICON_BUTTON
  image: Image
  accessibility_label: string
  action: {
    type: string
  }
}

export type IconSpotWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.ICON_SPOT
  icon_spot: {
    icon: {
      image_tag: string
      tint_color: 'text' | 'textMinor'
    }
    background_color: 'controlMinor'
  }
}

export type ImageWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.IMAGE
  background_color: string
  height: number
  text: {
    items: Array<TextWidgetConfig>
  }
  image: Image
}

export type NavWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.NAV
}

export type PaymentMethodWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.PAYMENT_METHOD
  title_style: {
    size?: number
    color?: string
  }
  additional_body: {
    align: 'trailing'
    title: {
      items: Array<TextWidgetConfig>
    }
  }
  available_payment_types: Array<'card' | 'cargocorp' | 'cash' | 'corp'>
  metrica_label?: EventGoal | string
}

export type RadioWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.RADIO
  enabled: boolean
  key: string
  value: string
  action?: ActionConfig
  metrica_label?: EventGoal | string
}

export type Badge = {
  text?: string
  text_color?: string
  background_color?: string
}

export type SegmentWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.SEGMENT
  action: {
    form_state_key: string
    options: Array<{
      selected_text: {
        title: {
          items: Array<TextWidgetConfig>
        }
      }
      unselected_text: {
        title: {
          items: Array<TextWidgetConfig>
        }
      }
      badge?: Badge
      value: number
      default: boolean
      is_available: boolean
      accessibility_info: {
        is_header: boolean
        content_description: string
      }
      metrica_label?: EventGoal | string
    }>
    metrica_label?: EventGoal | string
  }
}

export type SlotWidgetBody = {
  /** Горизонтальное выравнивание */
  align: 'leading' | 'center' | 'trailing'
  /** Тайтл */
  title?: {
    items: Array<TextWidgetConfig>
  }
  /** Кэпшн */
  subtitle?: {
    items: Array<TextWidgetConfig>
  }
}

export type SlotWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.SLOT
  accessibility_info?: {is_header: boolean}
  slot: {
    action?: ActionConfig
    /** Левая часть слота */
    lead?: WidgetConfig
    /** Левая часть слота после lead */
    body1?: SlotWidgetBody
    /** Середина слота */
    body2?: SlotWidgetBody
    /** Правая часть слота перед трейлами */
    body3?: SlotWidgetBody
    /** Первый трейл */
    trail1?: WidgetConfig
    /** Второй трейл */
    trail2?: WidgetConfig
  }
  className?: string
}

export type SwitchWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.SWITCH
  enabled: boolean
  key: string
  /**
   * индикатор инверсии: если приходит false, нужно инвертировать отправляемое значение
   * пример: skip_door_to_door
   * */
  value: boolean
  metrica_label?: EventGoal | string
}

type Tag = {
  metrica_label?: EventGoal | string
  text: string
  icon?: Image
  action: ActionConfig
}

export type TagLineWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.TAG_LINE
  tags: Array<Tag>
}

export type TextWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.TEXT
  text: string
  font_size: number
  font_weight?: TypographyWeights
  color: string
}

export type DropdownListWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.DROPDOWN_LIST
  form_state_key: string
  options: {
    label: {
      items: TextWidgetConfig[]
    }
    value: string
    is_default?: boolean
  }[]
  placeholder?: {
    items: TextWidgetConfig[]
  }
}

export type TextInputWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.TEXT_INPUT
  height: number
  placeholder: string
  form_state_key: string
}

export type PhoneInputWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.PHONE_INPUT
  height: number
  placeholder: string
  form_state_key: string
}

export type CostDetailWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.COST_DETAIL
  dot_style: {
    font_size?: number
    color?: string
  }
  lead_text?: {
    items: Array<TextWidgetConfig>
  }
  trail_text?: {
    items: Array<TextWidgetConfig>
  }
}

export type CounterWidgetConfig = BaseWidgetConfig & {
  type: WidgetType.COUNTER
  action: CounterActionConfig
}

export type WidgetConfig =
  | ActionButtonWidgetConfig
  | AddressWidgetConfig
  | DividerWidgetConfig
  | FormWidgetConfig
  | HeaderWidgetConfig
  | IconButtonWidgetConfig
  | IconSpotWidgetConfig
  | ImageWidgetConfig
  | NavWidgetConfig
  | PaymentMethodWidgetConfig
  | RadioWidgetConfig
  | SegmentWidgetConfig
  | SlotWidgetConfig
  | SwitchWidgetConfig
  | TagLineWidgetConfig
  | TextWidgetConfig
  | DropdownListWidgetConfig
  | IconWidgetConfig
  | TextInputWidgetConfig
  | PhoneInputWidgetConfig
  | CostDetailWidgetConfig
  | CounterWidgetConfig
  | VerticalsWidgetConfig

type AbstractConfig<T extends WidgetConfig> = {
  config: T
}
export type FormWidgetProps = AbstractConfig<FormWidgetConfig>
export type ActionButtonWidgetProps = AbstractConfig<ActionButtonWidgetConfig> & {
  sentinelRef: React.RefObject<HTMLElement>
}
export type AddressWidgetProps = AbstractConfig<AddressWidgetConfig>
export type VerticalsWidgetProps = AbstractConfig<VerticalsWidgetConfig>
export type SlotWidgetProps = AbstractConfig<SlotWidgetConfig>
export type DividerWidgetProps = AbstractConfig<DividerWidgetConfig>
export type DropdownListWidgetProps = AbstractConfig<DropdownListWidgetConfig>
export type HeaderWidgetProps = AbstractConfig<HeaderWidgetConfig>
export type IconWidgetProps = AbstractConfig<IconWidgetConfig>
export type IconButtonWidgetProps = AbstractConfig<IconButtonWidgetConfig>
export type IconSpotWidgetProps = AbstractConfig<IconSpotWidgetConfig>
export type ImageWidgetProps = AbstractConfig<ImageWidgetConfig>
export type PhoneInputWidgetProps = AbstractConfig<PhoneInputWidgetConfig>
export type TextInputWidgetProps = AbstractConfig<TextInputWidgetConfig>
export type NavWidgetProps = AbstractConfig<NavWidgetConfig>
export type PaymentMethodWidgetProps = AbstractConfig<PaymentMethodWidgetConfig>
export type RadioWidgetProps = AbstractConfig<
  RadioWidgetConfig & {
    name: string
  }
>
export type SegmentWidgetProps = AbstractConfig<SegmentWidgetConfig> & {
  className?: string
}
export type SwitchWidgetProps = AbstractConfig<
  SwitchWidgetConfig & {
    name: string
  }
>
export type TagLineWidgetProps = AbstractConfig<TagLineWidgetConfig>
export type TextWidgetProps = AbstractConfig<TextWidgetConfig>
export type CounterWidgetProps = AbstractConfig<CounterWidgetConfig>
export type CostDetailWidgetProps = AbstractConfig<CostDetailWidgetConfig>
